import React, { useState, useEffect, useContext } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { DirectionContext } from "../App";
import { updateTermsAgreement } from "../utils/terms";

const NewsProofSubmission = () => {
  const { navigateTo } = useContext(DirectionContext);

  // 아코디언 상태 관리
  const [expandedItems, setExpandedItems] = useState({
    registration: false,
    settlement: false,
    disclaimer: false,
    evidence: false,
  });

  // 전체 동의 체크박스 상태 관리
  const [allAgreed, setAllAgreed] = useState(false);

  // 아코디언 토글
  const toggleItem = (itemKey) => {
    setExpandedItems((prev) => ({
      ...prev,
      [itemKey]: !prev[itemKey],
    }));
  };

  // 전체 동의 체크박스 토글
  const toggleAllAgreed = () => {
    setAllAgreed((prev) => !prev);
  };

  // 페이지 로드 시 스크롤 위치를 맨 위로 초기화
  useEffect(() => {
    window.scrollTo(0, 0);
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
  }, []);

  // 다음 페이지로 이동
  const handleNext = async () => {
    if (!allAgreed) {
      return;
    }

    // 유의사항 동의(유의사항/성공보수 방식/증거자료 관련(제3자 제공)) 동의를 서버에 반영
    await updateTermsAgreement({
      hasAgreedToNotice: true,
      hasAgreedToSuccessFee: true,
    });

    // 신청서는 이미 ScreenshotSubmission.js에서 생성되었으므로 바로 다음 페이지로 이동
    navigateTo("/agreement");
  };

  // 애니메이션 변수
  const pageVariants = {
    initial: {
      opacity: 0,
    },
    in: {
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
    out: {
      opacity: 0,
      transition: {
        duration: 0.3,
      },
    },
  };

  const contentVariants = {
    initial: {
      opacity: 0,
      y: 20,
    },
    in: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        delay: 0.2,
        staggerChildren: 0.1,
      },
    },
  };

  // 유의사항 내용 렌더링 함수
  const renderNoticeContent = (key) => {
    switch (key) {
      case "registration":
        return (
          <NoticeText>
            본 집단소송은 쿠팡 개인정보 유출사고가 발생한 2025년 6월 24일 이전에
            쿠팡에 회원가입한 사용자 및 쿠팡 측(1577-7011)으로부터 2025년 11월
            말부터 2025년 12월 초 개인정보유출통지 문자를 받으신 분이면 누구나
            참여하실 수 있습니다
          </NoticeText>
        );
      case "settlement":
        return (
          <NoticeText>
            승소하여 법무법인 더 에이치 황해가 판결금을 대리 수령하는 경우,
            판결금에서 <BoldText>성공보수 10%(부가세 별도)</BoldText>를 공제한
            후, 나머지 금액을 집단소송 신청인이 지정한 계좌로 입금해드립니다.
          </NoticeText>
        );
      case "disclaimer":
        return (
          <NoticeText>
            본 집단소송은 사실관계와 법률적 판단에 따라 결과가 달라질 수 있으며,
            소송 결과를 확정적으로 보장드리기 어려움을 안내드립니다.
            {"\n\n"}
            변호인단은 적법한 절차에 따라 통상적인 주의의무와 전문적 판단으로
            사건을 수행하며, 그 범위 내에서 발생하는 소송 결과에 대하여는 책임을
            부담하지 않습니다.
          </NoticeText>
        );
      case "evidence":
        return (
          <NoticeText>
            현재 제출하시는 증거자료를 바탕으로 접수가 진행되며, 향후 쿠팡 측의
            대응 또는 법원의 요구에 따라 추가 증거자료 제출이 필요할 수
            있습니다.
          </NoticeText>
        );
      default:
        return null;
    }
  };

  return (
    <PageBackground>
      <Container
        variants={pageVariants}
        initial="initial"
        animate="in"
        exit="out"
      >
        <Content variants={contentVariants} initial="initial" animate="in">
          <Header>
            <SubTitle>유의사항</SubTitle>
            <Description>
              집단소송 참여를 위해
              <br />
              필요한 동의사항을 확인해주세요
            </Description>
          </Header>

          <NoticeList>
            {/* 가입 시점 요건 */}
            <NoticeItem>
              <NoticeHeader
                onClick={() => toggleItem("registration")}
                isExpanded={expandedItems.registration}
              >
                <NoticeTitle>가입 시점 요건</NoticeTitle>
                <ChevronIcon
                  src="/png/arrow.png"
                  alt="화살표"
                  isExpanded={expandedItems.registration}
                />
              </NoticeHeader>
              <NoticeContentWrapper
                isExpanded={expandedItems.registration}
                initial={false}
                animate={{
                  maxHeight: expandedItems.registration ? "500px" : 0,
                  opacity: expandedItems.registration ? 1 : 0,
                }}
                transition={{
                  maxHeight: {
                    duration: 0.5,
                    ease: [0.4, 0, 0.2, 1],
                  },
                  opacity: {
                    duration: 0.4,
                    ease: "easeInOut",
                    delay: expandedItems.registration ? 0.2 : 0,
                  },
                }}
              >
                <NoticeContentInner>
                  {renderNoticeContent("registration")}
                </NoticeContentInner>
              </NoticeContentWrapper>
            </NoticeItem>

            {/* 승소 시 성공보수 및 정산 방식 */}
            <NoticeItem>
              <NoticeHeader
                onClick={() => toggleItem("settlement")}
                isExpanded={expandedItems.settlement}
              >
                <NoticeTitle>승소 시 성공보수 및 정산 방식</NoticeTitle>
                <ChevronIcon
                  src="/png/arrow.png"
                  alt="화살표"
                  isExpanded={expandedItems.settlement}
                />
              </NoticeHeader>
              <NoticeContentWrapper
                isExpanded={expandedItems.settlement}
                initial={false}
                animate={{
                  maxHeight: expandedItems.settlement ? "500px" : 0,
                  opacity: expandedItems.settlement ? 1 : 0,
                }}
                transition={{
                  maxHeight: {
                    duration: 0.5,
                    ease: [0.4, 0, 0.2, 1],
                  },
                  opacity: {
                    duration: 0.4,
                    ease: "easeInOut",
                    delay: expandedItems.settlement ? 0.2 : 0,
                  },
                }}
              >
                <NoticeContentInner>
                  {renderNoticeContent("settlement")}
                </NoticeContentInner>
              </NoticeContentWrapper>
            </NoticeItem>

            {/* 면책고지 */}
            {/* <NoticeItem>
              <NoticeHeader
                onClick={() => toggleItem("disclaimer")}
                isExpanded={expandedItems.disclaimer}
              >
                <NoticeTitle>면책고지</NoticeTitle>
                <ChevronIcon
                  src="/png/arrow.png"
                  alt="화살표"
                  isExpanded={expandedItems.disclaimer}
                />
              </NoticeHeader>
              <NoticeContentWrapper
                isExpanded={expandedItems.disclaimer}
                initial={false}
                animate={{
                  maxHeight: expandedItems.disclaimer ? "500px" : 0,
                  opacity: expandedItems.disclaimer ? 1 : 0,
                }}
                transition={{
                  maxHeight: {
                    duration: 0.5,
                    ease: [0.4, 0, 0.2, 1],
                  },
                  opacity: {
                    duration: 0.4,
                    ease: "easeInOut",
                    delay: expandedItems.disclaimer ? 0.2 : 0,
                  },
                }}
              >
                <NoticeContentInner>
                  {renderNoticeContent("disclaimer")}
                </NoticeContentInner>
              </NoticeContentWrapper>
            </NoticeItem> */}

            {/* 증거자료 관련 안내 */}
            <NoticeItem>
              <NoticeHeader
                onClick={() => toggleItem("evidence")}
                isExpanded={expandedItems.evidence}
              >
                <NoticeTitle>증거자료 관련 안내</NoticeTitle>
                <ChevronIcon
                  src="/png/arrow.png"
                  alt="화살표"
                  isExpanded={expandedItems.evidence}
                />
              </NoticeHeader>
              <NoticeContentWrapper
                isExpanded={expandedItems.evidence}
                initial={false}
                animate={{
                  maxHeight: expandedItems.evidence ? "500px" : 0,
                  opacity: expandedItems.evidence ? 1 : 0,
                }}
                transition={{
                  maxHeight: {
                    duration: 0.5,
                    ease: [0.4, 0, 0.2, 1],
                  },
                  opacity: {
                    duration: 0.4,
                    ease: "easeInOut",
                    delay: expandedItems.evidence ? 0.2 : 0,
                  },
                }}
              >
                <NoticeContentInner>
                  {renderNoticeContent("evidence")}
                </NoticeContentInner>
              </NoticeContentWrapper>
            </NoticeItem>
          </NoticeList>

          <AllAgreeContainer>
            <CheckboxContainer onClick={toggleAllAgreed}>
              <Checkbox checked={allAgreed}>
                {allAgreed && <CheckIcon src="/png/checked.png" alt="체크" />}
              </Checkbox>
              <CheckboxLabel>
                위 내용을 모두 확인하고 동의합니다.{" "}
                <RequiredText>[필수]</RequiredText>
              </CheckboxLabel>
            </CheckboxContainer>
          </AllAgreeContainer>

          <ButtonsContainer>
            <NextButton
              onClick={handleNext}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              disabled={!allAgreed}
            >
              다음으로
            </NextButton>
          </ButtonsContainer>
        </Content>
      </Container>
    </PageBackground>
  );
};

// 스타일 컴포넌트
const PageBackground = styled.div`
  width: 100%;
  min-height: 100vh;
  background-color: #f6f8ff;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  @media (min-width: 769px) {
    background-color: #f6f8ff;
  }
`;

const Container = styled(motion.div)`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  background-color: #f6f8ff;

  /* 모바일 디바이스 */
  @media (max-width: 768px) {
    min-height: 100vh;
    box-shadow: none;
    padding: 0 0 100px 0;
    max-width: 100%;
    margin: 0;
    border-radius: 0;
  }

  /* 데스크탑 디바이스 */
  @media (min-width: 769px) {
    border-radius: 20px;
    min-height: auto;
    max-height: none;
    max-width: 470px;
    margin: 0 auto;
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.2);
    padding: 20px;
  }
`;

const Content = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  text-align: left;
  width: 100%;
  position: relative;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 60px 20px 80px;
    margin-top: 40px;
  }

  @media (min-width: 769px) {
    padding: 40px 30px 40px 30px;
  }
`;

const Header = styled.div`
  width: 100%;
  margin-bottom: 20px;
  color: #333;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const SubTitle = styled.h2`
  font-size: 28px;
  font-weight: 700;
  line-height: 1.3;
  margin-bottom: 10px;
  word-break: keep-all;
  color: #333;
  text-align: left;
  width: 100%;
`;

const Description = styled.p`
  font-size: 16px;
  color: #666;
  margin-bottom: 10px;
  line-height: 1.4;
`;

const NoticeList = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    margin-bottom: 40px;
  }
`;

const NoticeItem = styled.div`
  background-color: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
`;

const NoticeHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  cursor: pointer;
`;

const NoticeTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 0;
`;

const ChevronIcon = styled.img`
  width: 16px;
  height: 16px;
  transition: transform 0.3s ease;
  transform: ${(props) =>
    props.isExpanded ? "rotate(0deg)" : "rotate(180deg)"};
`;

const NoticeContentWrapper = styled(motion.div)`
  overflow: hidden;
  transform-origin: top;
  will-change: max-height, opacity;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
`;

const NoticeContentInner = styled.div`
  padding: 0 20px 20px 20px;
`;

const NoticeText = styled.p`
  font-size: 14px;
  color: #666;
  line-height: 1.6;
  margin: 0 0 16px 0;
  white-space: pre-line;
`;

const BoldText = styled.span`
  font-weight: 700;
  color: #333;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 8px 0;
`;

const Checkbox = styled.div`
  width: 22px;
  height: 22px;
  border: 2px solid ${(props) => (props.checked ? "#0075FF" : "#ddd")};
  border-radius: 4px;
  background-color: ${(props) => (props.checked ? "#0075FF" : "white")};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
  flex-shrink: 0;
  transition: all 0.2s ease;
`;

const CheckIcon = styled.img`
  width: 14px;
  height: 14px;
`;

const CheckboxLabel = styled.span`
  font-size: 14px;
  color: #333;
  font-weight: 500;
`;

const AllAgreeContainer = styled.div`
  width: 100%;
  max-width: 400px;
  margin: 0 auto 20px auto;
  padding: 0;
`;

const RequiredText = styled.span`
  color: #3855e2;
  font-weight: 600;
`;

const ButtonsContainer = styled.div`
  max-width: 400px;
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  margin-top: 0px;
  padding: 0 0px;
  background-color: #f8f8f8;
  margin: 0 auto;

  @media (max-width: 768px) {
    margin-top: 0px;
    padding-bottom: 40px;
  }

  @media (min-width: 769px) {
    padding: 0;
    background-color: transparent;
    justify-content: center;
  }
`;

const NextButton = styled(motion.button)`
  flex: 1;
  display: flex;
  height: 56px;
  padding: 0px 14px;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  border-radius: 20px;
  background: ${(props) => (props.disabled ? "#A9DAFF" : "#3855E2")};
  color: ${(props) => (props.disabled ? "#CCEAFF" : "white")};
  font-size: 16px;
  font-weight: 600;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  border: none;
  opacity: 1;
  pointer-events: auto;
`;

export default NewsProofSubmission;
